import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  MapPin,
  Phone,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Package,
  FileText,
  Zap,
  Tag,
  Car,
  Navigation,
  Clock,
  Zap as Lightning,
  CalendarDays,
  Loader2,
  User as UserIcon,
  LogIn,
  Bookmark,
  Crown,
  Plus,
  Star,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { fmt12 } from "@/lib/time";

import { API } from "@/lib/api";
import { navigateAfterFirstBooking } from "@/lib/install";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import UserAvatar from "@/components/UserAvatar";
import { drivingDistanceMiles, getCurrentPosition, MAX_DISTANCE_MILES, searchAddress } from "@/lib/geo";
import LiveMap from "@/components/LiveMap";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import SupportTicketDialog from "@/components/SupportTicketDialog";
import RedeemPointsPrompt from "@/components/RedeemPointsPrompt";

const SUPPORT_PHONE = "1 (484) 577-3677";

const SERVICE_ICONS = {
  ride: Car,
  envelope: FileText,
  small_package: Package,
  medium_package: Package,
  large_package: Package,
  rush_delivery: Lightning
};

const pad = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

export default function CustomerBooking() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  // Franchise-aware support phone: default operator phone unless the tenant
  // overrides it in their branding profile.
  const supportPhone = tenant?.branding?.support_phone || SUPPORT_PHONE;
  const isFranchiseeTenant = tenant && tenant.is_default === false;
  // Signup-bonus banner data (only meaningful for customers with unspent bonus)
  const [signupBonus, setSignupBonus] = useState(null); // { remaining, expires_at, points }
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'customer') {
      setSignupBonus(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/me/loyalty`)
      .then((r) => {
        if (cancelled) return;
        const rem = Number(r.data?.signup_bonus_remaining || 0);
        if (rem > 0) {
          setSignupBonus({
            remaining: rem,
            expires_at: r.data.signup_bonus_expires_at,
            points: Number(r.data?.points || 0),
          });
        } else {
          setSignupBonus(null);
        }
      })
      .catch(() => setSignupBonus(null));
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.role, user?.id]);
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  // First-page category toggle: "ride" or "delivery". Rideshare is the
  // default because it's the higher-frequency use-case and the main
  // pitch to shift workers. Persisted only for the current session so a
  // deliberate driver-side switch doesn't stick after refresh.
  const [serviceCategory, setServiceCategory] = useState("ride");
  const [userLocation, setUserLocation] = useState(null); // geolocation anchor for narrowing search
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [maxPlanDiscount, setMaxPlanDiscount] = useState(0); // highest Link Pro % off (for upsell)
  const [bookingComplete, setBookingComplete] = useState(false);
  const [completedBookingId, setCompletedBookingId] = useState(null);

  // Addresses
  const [pickup, setPickup] = useState({ address: "", coords: null, city: "", state: "" });
  const [dropoff, setDropoff] = useState({ address: "", coords: null, city: "", state: "" });
  const [stops, setStops] = useState([]);  // intermediate waypoints: [{address, coords, city, state}]
  const [distanceMiles, setDistanceMiles] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);

  // Schedule
  const [scheduleMode, setScheduleMode] = useState("asap"); // "asap" | "later"
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("");

  // Payment method
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // 'stripe' | 'jim' | 'cash' | 'credits'
  const [walletCents, setWalletCents] = useState(0);
  // Partial wallet redemption — when ON, apply the customer's available
  // credits up to the trip total and charge the remainder via paymentMethod
  const [useCredits, setUseCredits] = useState(false);

  // Promo code
  const [promoCode, setPromoCode] = useState("");
  const [promoData, setPromoData] = useState(null);    // { code, discount_dollars, new_total, ... } when applied
  const [promoApplying, setPromoApplying] = useState(false);
  const applyPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) { toast.error("Enter a promo code"); return; }
    if (!quote?.finalPrice) { toast.error("Get a quote first"); return; }
    setPromoApplying(true);
    try {
      const r = await axios.post(`${API}/promo-codes/validate`, { code, total: quote.finalPrice });
      setPromoData(r.data);
      toast.success(`${r.data.code} applied — saved $${r.data.discount_dollars.toFixed(2)}`);
    } catch (e) {
      setPromoData(null);
      toast.error(e.response?.data?.detail || "Invalid promo code");
    } finally {
      setPromoApplying(false);
    }
  };
  const removePromoCode = () => { setPromoData(null); setPromoCode(""); sessionStorage.removeItem('link_ref_code'); };

  // Capture ?ref=CODE from the share link → stash in sessionStorage so it
  // survives the multi-step booking flow, and auto-fill the promo input.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get('ref');
    if (refFromUrl) {
      const code = refFromUrl.toUpperCase().trim();
      sessionStorage.setItem('link_ref_code', code);
      setPromoCode(code);
      // Strip ?ref= from URL so refreshing doesn't re-trigger logic
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    } else {
      const stashed = sessionStorage.getItem('link_ref_code');
      if (stashed && !promoCode) setPromoCode(stashed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply a stashed/captured promo code once we have a quote
  useEffect(() => {
    const stashed = sessionStorage.getItem('link_ref_code');
    if (stashed && quote?.finalPrice && !promoData) {
      axios.post(`${API}/promo-codes/validate`, { code: stashed, total: quote.finalPrice })
        .then(r => {
          setPromoData(r.data);
          toast.success(`🎟 ${r.data.code} auto-applied — saved $${r.data.discount_dollars.toFixed(2)}`);
        })
        .catch(() => sessionStorage.removeItem('link_ref_code'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.finalPrice]);

  // Reapply when quote changes (e.g. distance recomputed)
  useEffect(() => {
    if (promoData?.code && quote?.finalPrice) {
      axios.post(`${API}/promo-codes/validate`, { code: promoData.code, total: quote.finalPrice })
        .then(r => setPromoData(r.data))
        .catch(() => setPromoData(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.finalPrice]);

  // Beta service area + availability
  const [serviceArea, setServiceArea] = useState(null); // { center:{lat,lng}, radius_miles, label }
  const [availability, setAvailability] = useState(null); // { asap_available, earliest_scheduled_iso, ... }
  // Busy time windows for the selected date — shown so the customer knows
  // which time slots are already taken before they hit submit.
  const [busySlots, setBusySlots] = useState([]); // [{start, end, duration_minutes}]
  const [busyCapacity, setBusyCapacity] = useState(1);

  useEffect(() => {
    axios.get(`${API}/service-area`).then(r => setServiceArea(r.data)).catch(() => {});
    axios.get(`${API}/availability`).then(r => setAvailability(r.data)).catch(() => {});
  }, []);

  // Fetch busy slots whenever the customer picks a date
  useEffect(() => {
    if (!scheduledDate) { setBusySlots([]); return; }
    const iso = scheduledDate.toISOString().slice(0, 10);
    axios.get(`${API}/bookings/busy-slots`, { params: { date: iso } })
      .then(r => { setBusySlots(r.data?.busy || []); setBusyCapacity(r.data?.capacity || 1); })
      .catch(() => { setBusySlots([]); });
  }, [scheduledDate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/me/credits`).then(r => setWalletCents(r.data?.balance_cents || 0)).catch(() => {});
  }, [isAuthenticated]);

  // Pickup outside Beta service area?
  const pickupMilesFromCenter = (() => {
    if (!serviceArea?.center || !pickup.coords) return null;
    const R = 3958.8;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(pickup.coords.lat - serviceArea.center.lat);
    const dLng = toRad(pickup.coords.lng - serviceArea.center.lng);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(serviceArea.center.lat)) * Math.cos(toRad(pickup.coords.lat)) *
      Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  })();
  const pickupOutsideArea = pickupMilesFromCenter != null && serviceArea && pickupMilesFromCenter > serviceArea.radius_miles;

  // Earliest scheduled time (driver pipeline aware) — refresh when component mounts
  const earliestSchedule = availability?.earliest_scheduled_iso ? new Date(availability.earliest_scheduled_iso) : null;
  const asapAvailable = availability?.asap_available !== false;

  // Contact + notes
  const [contact, setContact] = useState({ customer_name: "", customer_phone: "", customer_email: "", notes: "" });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load services");
      } finally {
        setLoading(false);
      }
    })();

    // Load Link Pro plans so we can show the "would be $X with Link Pro" upsell
    axios.get(`${API}/plans`).then((r) => {
      const top = (r.data || []).reduce(
        (acc, p) => Math.max(acc, Number(p.discount_pct) || 0),
        0
      );
      setMaxPlanDiscount(top);
    }).catch(() => {});
  }, []);

  // Load saved addresses when user is logged in
  useEffect(() => {
    if (!isAuthenticated) {
      setSavedAddresses([]);
      return;
    }
    axios.get(`${API}/me/addresses`)
      .then((res) => {
        const list = res.data || [];
        setSavedAddresses(list);
        // Auto-fill pickup with default saved address (first in list for now)
        const def = list.find((a) => a.is_default) || list[0];
        if (def && def.coords && !pickup.coords) {
          setPickup({ address: def.address, coords: def.coords, city: def.city || '', state: def.state || '' });
        }
      })
      .catch(() => setSavedAddresses([]));
  }, [isAuthenticated]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Best-effort geolocation to narrow Nominatim search to local area
  useEffect(() => {
    getCurrentPosition().then((pos) => { if (pos) setUserLocation(pos); });
  }, []);

  // Book again prefill from session storage
  useEffect(() => {
    const raw = sessionStorage.getItem('link_prefill');
    if (!raw) return;
    sessionStorage.removeItem('link_prefill');
    try {
      const p = JSON.parse(raw);
      if (p.service_type && services.length > 0) {
        const svc = services.find((s) => s.id === p.service_type);
        if (svc) setSelectedService(svc);
      }
      if (p.pickup) setPickup({ address: p.pickup.address, coords: p.pickup.coords, city: p.pickup.city || '', state: '' });
      if (p.dropoff) setDropoff({ address: p.dropoff.address, coords: p.dropoff.coords, city: p.dropoff.city || '', state: '' });
      if (p.service_type) setStep(2);
    } catch { /* ignore */ }
  }, [services]);

  // Auto-fill contact from logged-in user
  useEffect(() => {
    if (user) {
      setContact((c) => ({
        ...c,
        customer_name: c.customer_name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        customer_phone: c.customer_phone || user.phone || '',
        customer_email: c.customer_email || user.email || ''
      }));
    }
  }, [user]);

  const saveCurrentAddress = async (addr, defaultLabel) => {
    if (!isAuthenticated || !addr.coords) return;
    const label = window.prompt("Save as…", defaultLabel);
    if (!label || !label.trim()) return;
    try {
      await axios.post(`${API}/me/addresses`, {
        label: label.trim(),
        address: addr.address,
        city: addr.city,
        state: addr.state,
        coords: addr.coords
      });
      const res = await axios.get(`${API}/me/addresses`);
      setSavedAddresses(res.data || []);
      toast.success(`Saved "${label.trim()}"`);
    } catch {
      toast.error("Failed to save");
    }
  };

  // Compute driving distance whenever both coords are set (sum of legs through stops)
  const lastRouteRef = useRef(null);
  useEffect(() => {
    if (!pickup.coords || !dropoff.coords) {
      setDistanceMiles(null);
      setRouteLoading(false);  // safety — never leave the spinner stuck if user clears an address
      return;
    }
    const validStops = stops.filter(s => s.coords);
    const waypoints = [pickup.coords, ...validStops.map(s => s.coords), dropoff.coords];
    const key = waypoints.map(c => `${c.lat},${c.lng}`).join("->");
    if (lastRouteRef.current === key) return;
    lastRouteRef.current = key;
    setRouteLoading(true);

    // Hard 12-second outer timeout — guarantees the spinner never deadlocks
    // even if OSRM, the fetch layer, or the .finally() handler misbehaves.
    const deadline = setTimeout(() => setRouteLoading(false), 12000);

    Promise.all(
      waypoints.slice(0, -1).map((from, i) => drivingDistanceMiles(from, waypoints[i + 1]))
    )
      .then((legs) => {
        if (legs.every(l => l != null)) {
          const total = legs.reduce((a, b) => a + b, 0);
          setDistanceMiles(Math.round(total * 10) / 10);
        } else {
          setDistanceMiles(null);
        }
      })
      .finally(() => {
        clearTimeout(deadline);
        setRouteLoading(false);
      });
  }, [pickup.coords, dropoff.coords, stops]);

  // Fetch quote whenever service or distance changes
  useEffect(() => {
    const run = async () => {
      if (!selectedService || distanceMiles == null) {
        setQuote(null);
        return;
      }
      setQuoteLoading(true);
      try {
        const scheduledDay = scheduleMode === "later" && scheduledDate
          ? DAYS[scheduledDate.getDay()]
          : DAYS[new Date().getDay()];
        const res = await axios.post(`${API}/quote`, {
          serviceType: selectedService.id,
          distanceMiles,
          city: dropoff.city || pickup.city,
          scheduledDay,
          extraStops: stops.length,
        });
        setQuote(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setQuoteLoading(false);
      }
    };
    run();
  }, [selectedService, distanceMiles, scheduleMode, scheduledDate, pickup.city, dropoff.city, stops.length]);

  // If driver becomes busy mid-flow, force users into Schedule mode + auto-fill earliest slot
  useEffect(() => {
    if (availability && !availability.asap_available && scheduleMode === "asap") {
      setScheduleMode("later");
      if (earliestSchedule) {
        setScheduledDate(earliestSchedule);
        const hh = String(earliestSchedule.getHours()).padStart(2, "0");
        const mm = String(earliestSchedule.getMinutes()).padStart(2, "0");
        setScheduledTime(`${hh}:${mm}`);
      }
    }
  }, [availability, scheduleMode, earliestSchedule]);

  const overLimit = distanceMiles != null && distanceMiles > MAX_DISTANCE_MILES;
  const canContinueFromAddresses = pickup.coords && dropoff.coords && distanceMiles != null && !overLimit && !pickupOutsideArea;
  // ASAP only available when (a) driver is free AND (b) customer has selected ASAP mode
  // Scheduled mode requires (date + time) AND that time is at or after the earliest free slot
  const canContinueFromSchedule = (() => {
    if (scheduleMode === "asap") return asapAvailable;
    if (!scheduledDate || !scheduledTime) return false;
    if (!earliestSchedule) return true;
    const picked = new Date(scheduledDate);
    const [h, m] = scheduledTime.split(":");
    picked.setHours(parseInt(h || "0", 10), parseInt(m || "0", 10), 0, 0);
    return picked >= earliestSchedule;
  })();
  const canSubmit = contact.customer_name.trim() && contact.customer_phone.trim() && canContinueFromAddresses && canContinueFromSchedule;

  const submitBooking = async () => {
    if (!canSubmit || !selectedService) return;
    setSubmitting(true);
    try {
      // Resolve any stop the customer typed but didn't pick from autocomplete.
      // Without this, those stops are silently dropped — which is exactly how
      // Christina's stops vanished. We geocode on-demand and bail with a clear
      // toast if a stop can't be located.
      const resolvedStops = [];
      for (let i = 0; i < stops.length; i++) {
        const s = stops[i];
        if (!s?.address || !s.address.trim()) continue;
        if (s.coords) {
          resolvedStops.push({ address: s.address, coords: s.coords });
          continue;
        }
        try {
          const results = await searchAddress(s.address, { limit: 1, anchor: pickup.coords || userLocation });
          if (results && results.length && results[0].coords) {
            resolvedStops.push({ address: results[0].label || s.address, coords: results[0].coords });
          } else {
            toast.error(`Stop ${i + 1} couldn't be located. Pick an address from the dropdown.`);
            setSubmitting(false);
            return;
          }
        } catch {
          toast.error(`Stop ${i + 1} couldn't be located. Pick an address from the dropdown.`);
          setSubmitting(false);
          return;
        }
      }
      const now = new Date();
      const asap = scheduleMode === "asap";
      // Partial wallet redemption: capture how much of the trip credits cover.
      // Mirror the same Stripe-minimum cap the UI displays so server + client
      // agree exactly on the deduction.
      const STRIPE_MIN_REMAINDER_CENTS = 100;
      const finalCents = Math.round(((promoData?.new_total ?? quote?.finalPrice) || 0) * 100);
      let creditsToApplyCents = 0;
      if (useCredits) {
        if (walletCents >= finalCents) {
          creditsToApplyCents = finalCents;
        } else if (paymentMethod === "stripe" && (finalCents - walletCents) < STRIPE_MIN_REMAINDER_CENTS) {
          creditsToApplyCents = Math.max(0, finalCents - STRIPE_MIN_REMAINDER_CENTS);
        } else {
          creditsToApplyCents = Math.min(walletCents, finalCents);
        }
      }
      const fullyCoveredByCredits = creditsToApplyCents >= finalCents && finalCents > 0;

      const payload = {
        customer_name: contact.customer_name.trim(),
        customer_phone: contact.customer_phone.trim(),
        customer_email: contact.customer_email.trim() || undefined,
        pickup_address: pickup.address,
        pickup_city: pickup.city,
        pickup_coords: pickup.coords,
        dropoff_address: dropoff.address,
        dropoff_city: dropoff.city,
        dropoff_coords: dropoff.coords,
        stops: resolvedStops,
        service_type: selectedService.id,
        estimated_price: promoData?.new_total ?? quote?.finalPrice,
        distance_miles: distanceMiles,
        scheduled_date: asap ? toISODate(now) : toISODate(scheduledDate),
        scheduled_time: asap ? `${pad(now.getHours())}:${pad(now.getMinutes())}` : scheduledTime,
        asap,
        notes: contact.notes.trim(),
        // If credits cover the entire trip, we send "credits" so the backend
        // marks the booking paid. Otherwise we still pass the chosen method
        // (stripe/jim/cash) for the REMAINDER.
        payment_method: fullyCoveredByCredits ? "credits" : paymentMethod,
        promo_code: promoData?.code,
        credits_to_apply_cents: creditsToApplyCents,
      };
      const res = await axios.post(`${API}/bookings`, payload, { timeout: 30000 });
      const newBookingId = res.data?.id;
      if (!newBookingId) throw new Error("Booking creation failed");

      // Stripe path — only when there's still money owed AFTER credits.
      if (paymentMethod === "stripe" && !fullyCoveredByCredits) {
        const origin = window.location.origin;
        let checkoutRes;
        try {
          checkoutRes = await axios.post(
            `${API}/payments/create-checkout`,
            {
              booking_id: newBookingId,
              origin_url: `${origin}/payment-success?booking_id=${newBookingId}`,
            },
            { timeout: 30000 },
          );
        } catch (checkoutErr) {
          // The booking IS created; Stripe checkout creation failed.
          // Don't strand the customer — let them retry payment from the
          // success/pending screen instead of resubmitting the whole booking.
          console.error("[create-checkout failed]", checkoutErr);
          const code = checkoutErr.code || checkoutErr.response?.status || "ERR";
          const detail = checkoutErr.response?.data?.detail || checkoutErr.message || "unknown";
          sessionStorage.setItem("link_pending_booking", newBookingId);
          setCompletedBookingId(newBookingId);
          setBookingComplete(true);
          toast.error(
            `Booking saved, but couldn't open Stripe (${code}: ${detail}). Tap "Retry payment" below.`,
            { duration: 12000 },
          );
          return;
        }
        // If credits exactly covered the trip on the backend, the endpoint
        // returns checkout_url: null with fully_covered_by_credits: true.
        if (checkoutRes.data?.fully_covered_by_credits) {
          setCompletedBookingId(newBookingId);
          setBookingComplete(true);
          toast.success("Booking confirmed — paid with credits");
          return;
        }
        if (checkoutRes.data?.checkout_url) {
          // Same-tab redirect (Stripe's recommended pattern — never popup-blocked)
          sessionStorage.setItem("link_pending_booking", newBookingId);
          sessionStorage.setItem("link_checkout_url", checkoutRes.data.checkout_url);
          window.location.assign(checkoutRes.data.checkout_url);
          return;
        }
        // No URL came back AND it wasn't fully credit-covered → show retry
        sessionStorage.setItem("link_pending_booking", newBookingId);
        setCompletedBookingId(newBookingId);
        setBookingComplete(true);
        toast.error(
          'Stripe didn\'t return a checkout URL. Tap "Retry payment" below, or pick JIM / Cash.',
          { duration: 12000 },
        );
        return;
      }

      setCompletedBookingId(newBookingId);
      setBookingComplete(true);
      toast.success(fullyCoveredByCredits ? "Booking confirmed — paid with credits" : "Booking submitted!");
    } catch (e) {
      console.error("[booking submit failed]", e);
      // Backend FastAPI returns either {detail: "string"} (HTTPException) or
      // {detail: [{msg, loc, ...}]} (Pydantic 422). Show the most helpful
      // message we can extract — otherwise fall back to a generic copy that
      // tells the customer to retry / contact us.
      const data = e.response?.data;
      let msg = "Failed to submit booking";
      if (data?.detail) {
        if (typeof data.detail === "string") {
          msg = data.detail;
        } else if (Array.isArray(data.detail) && data.detail[0]?.msg) {
          msg = data.detail.map(d => d.msg).join("; ");
        }
      } else if (e.message && !e.response) {
        msg = `Network error: ${e.message}`;
      }
      if (e.response?.status) {
        msg = `${msg} (${e.response.status})`;
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const resetFlow = () => {
    sessionStorage.removeItem('link_checkout_url');
    sessionStorage.removeItem('link_pending_booking');
    setBookingComplete(false);
    setCompletedBookingId(null);
    setStep(1);
    setSelectedService(null);
    setPickup({ address: "", coords: null, city: "", state: "" });
    setDropoff({ address: "", coords: null, city: "", state: "" });
    setStops([]);
    setDistanceMiles(null);
    setScheduleMode("asap");
    setScheduledDate(null);
    setScheduledTime("");
    setContact({ customer_name: "", customer_phone: "", customer_email: "", notes: "" });
    setQuote(null);
  };

  // ---------- Booking Complete Screen ----------
  if (bookingComplete) {
    const checkoutUrl = sessionStorage.getItem('link_checkout_url');
    const isStripePending = paymentMethod === 'stripe' && checkoutUrl;
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
        <Card className="bg-[#121212] border-white/5 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className={`w-16 h-16 rounded-full ${isStripePending ? 'bg-[#0047FF]/20' : 'bg-[#00E676]/10'} flex items-center justify-center mx-auto mb-6`}>
              <CheckCircle className={`w-8 h-8 ${isStripePending ? 'text-[#4D88FF]' : 'text-[#00E676]'}`} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "Outfit, system-ui" }}>
              {isStripePending ? "Almost done — finish payment" : "Booking Submitted"}
            </h2>
            <p className="text-white/60 mb-4">
              {selectedService?.name} · {scheduleMode === "asap" ? "ASAP" : `${toISODate(scheduledDate)} at ${scheduledTime}`}
            </p>
            {quote && (
              <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 mb-6">
                <p className="text-xs uppercase tracking-wider text-white/40 font-bold">Estimated Total</p>
                <p className={`text-3xl font-black mt-1 ${isStripePending ? 'text-[#4D88FF]' : 'text-[#00E676]'}`} style={{ fontFamily: "Outfit, system-ui" }}>
                  ${quote.finalPrice.toFixed(2)}
                </p>
                {distanceMiles != null && (
                  <p className="text-xs text-white/50 mt-1">
                    {distanceMiles.toFixed(1)} mi driving distance
                  </p>
                )}
              </div>
            )}
            {isStripePending && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3 mb-4 text-left text-xs text-amber-200/80">
                <p className="font-semibold mb-1">Payment opened in a new tab.</p>
                <p>If you don't see it, your browser may be blocking it. Tap the blue button below — and if it stays blank, switch to a different browser or try Incognito mode. Your booking is saved either way.</p>
              </div>
            )}
            <div className="space-y-3">
              {/* Retry payment — for the case where the booking was created but
                  Stripe Checkout creation failed. Hits create-checkout again
                  with the same booking_id so the customer doesn't lose any data. */}
              {completedBookingId && (
                <Button
                  onClick={async () => {
                    try {
                      const origin = window.location.origin;
                      const r = await axios.post(
                        `${API}/payments/create-checkout`,
                        {
                          booking_id: completedBookingId,
                          origin_url: `${origin}/payment-success?booking_id=${completedBookingId}`,
                        },
                        { timeout: 30000 },
                      );
                      if (r.data?.fully_covered_by_credits) {
                        toast.success("Paid with credits");
                        navigateAfterFirstBooking(navigate, completedBookingId);
                        return;
                      }
                      if (r.data?.checkout_url) {
                        window.location.assign(r.data.checkout_url);
                        return;
                      }
                      toast.error("Stripe didn't return a URL. Contact us so we can help finish this booking.", { duration: 9000 });
                    } catch (err) {
                      const code = err.code || err.response?.status || "ERR";
                      const detail = err.response?.data?.detail || err.message || "unknown";
                      toast.error(`Retry failed (${code}: ${detail}). Try again or contact us.`, { duration: 12000 });
                    }
                  }}
                  className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                  data-testid="retry-payment-button"
                >
                  Retry payment
                </Button>
              )}
              {isStripePending && (
                <Button
                  onClick={() => {
                    sessionStorage.removeItem('link_checkout_url');
                    window.location.assign(checkoutUrl);
                  }}
                  variant="outline"
                  className="w-full border-white/15 text-white/80 hover:bg-white/5"
                  data-testid="reopen-checkout-button"
                >
                  Re-open old checkout link
                </Button>
              )}
              {completedBookingId && (
                <Button
                  onClick={() => navigateAfterFirstBooking(navigate, completedBookingId)}
                  className={`w-full ${isStripePending ? 'bg-[#0047FF]/15 text-[#4D88FF] hover:bg-[#0047FF]/25 border border-[#0047FF]/30' : 'bg-[#0047FF] hover:bg-[#003BE0] text-white shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0'} font-bold rounded-xl`}
                  data-testid="track-order-button"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Track My Order
                </Button>
              )}
              <p className="text-sm text-white/50">Questions? Contact us:</p>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.open(`tel:${supportPhone}`, "_self")}
                  className="flex-1 bg-[#0047FF]/10 hover:bg-[#0047FF]/20 text-[#4D88FF] border border-[#0047FF]/30"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
                <Button
                  onClick={() => window.open(`sms:${supportPhone}`, "_self")}
                  variant="outline"
                  className="flex-1 border-white/10 text-white/70 hover:bg-white/5"
                >
                  Text
                </Button>
              </div>
              <Button onClick={resetFlow} variant="ghost" className="w-full text-white/50">
                Book Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Main Wizard ----------
  return (
    <div className="min-h-screen bg-[#050505]">

      {/* Welcome signup-bonus banner — only when the customer still has unspent
          bonus points. Hides automatically once they redeem or it expires. */}
      {signupBonus && signupBonus.remaining > 0 && (() => {
        let daysLeft = null;
        if (signupBonus.expires_at) {
          const ms = new Date(signupBonus.expires_at).getTime() - Date.now();
          if (Number.isFinite(ms) && ms > 0) {
            daysLeft = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
          }
        }
        return (
          <div
            className="sticky top-0 z-40 bg-gradient-to-r from-[#FFD369]/15 to-[#FFD369]/5 backdrop-blur border-b border-[#FFD369]/30 px-4 py-2.5 flex items-center justify-between gap-3"
            data-testid="signup-bonus-banner"
          >
            <p className="text-xs sm:text-sm text-[#FFD369] truncate">
              <span className="font-bold">Welcome gift:</span> {signupBonus.remaining} pts =
              <span className="font-bold"> $5 off your first ride</span>
              {daysLeft !== null && <span className="text-[#FFD369]/70"> · {daysLeft}d left</span>}
            </p>
            <button
              onClick={() => navigate('/account?tab=loyalty')}
              className="text-[11px] sm:text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg bg-[#FFD369] hover:bg-[#FFC52E] text-black whitespace-nowrap"
              data-testid="signup-bonus-redeem-btn"
            >
              Redeem →
            </button>
          </div>
        );
      })()}

      {/* Driver-account banner — anyone logged in as driver or admin who lands
          here (e.g. via browser back from /driver/bookings) sees a one-tap
          return to the driver hub. Without this banner the driver/admin can
          get visually trapped on the customer "buy" screen. */}
      {isAuthenticated && (user?.role === 'driver' || user?.role === 'admin') && (
        <div
          className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 px-4 py-2 flex items-center justify-between gap-3"
          data-testid="driver-return-banner"
        >
          <p className="text-xs text-zinc-300 truncate">
            <span className="text-[#4D88FF] font-semibold">Driver view</span> — back to your jobs?
          </p>
          <button
            onClick={() => navigate('/driver/bookings')}
            className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg bg-[#4D88FF] hover:bg-[#6699FF] text-white whitespace-nowrap"
            data-testid="driver-return-button"
          >
            Driver hub →
          </button>
        </div>
      )}

      {/* Cinematic Masthead */}
      <header className="relative bg-[#050505] overflow-hidden rounded-b-[32px] border-b border-white/5 shadow-[0_4px_40px_-10px_rgba(0,71,255,0.15)]">
        <div className="relative px-5 pt-4 pb-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            {/* Live Network indicator inline on the left — replaces the
                stacked logo+pulse row so the hero can move up on iPhone
                and the Ride/Delivery service picker stays on-screen. */}
            <div className="flex items-center gap-2 shrink-0" data-testid="live-network-indicator">
              <div className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse shadow-[0_0_10px_rgba(0,230,118,0.9)]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-bold">Live Network</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/help')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-xs font-medium transition"
                data-testid="header-help-btn"
                aria-label="How Link Ride works"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                How it works
              </button>
              <button
                onClick={() => navigate('/plans')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFD369]/10 hover:bg-[#FFD369]/15 border border-[#FFD369]/30 text-[#FFD369] text-xs font-bold transition"
                data-testid="header-link-pass-btn"
              >
                <Crown className="w-3.5 h-3.5" />
                Link Pro
              </button>
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/account')}
                  className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs font-medium transition"
                  data-testid="account-button"
                >
                  <UserAvatar user={user} size={24} />
                  <span className="hidden sm:inline pr-2">{user?.preferred_name || user?.first_name || 'Account'}</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login?redirect=/')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs font-medium transition"
                  data-testid="signin-button"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Sign in
                </button>
              )}
            </div>
          </div>

          {/* Brand banner — for the flagship Link Ride tenant this is the full
              hero image. For franchisee tenants we render their logo +
              display name + tagline instead so their brand carries through
              the whole customer flow. */}
          {isFranchiseeTenant ? (
            <div
              className="mx-auto w-full max-w-[720px] rounded-xl border border-white/10 bg-black/40 p-6 text-center space-y-3"
              data-testid="tenant-brand-hero"
              style={{ boxShadow: `0 8px 40px -12px ${tenant?.branding?.primary_color || '#0047FF'}55` }}
            >
              {tenant?.branding?.logo_url ? (
                <img
                  src={tenant.branding.logo_url}
                  alt={`${tenant.display_name} logo`}
                  className="mx-auto max-h-24 object-contain"
                  data-testid="tenant-logo"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : null}
              <h1
                className="text-3xl sm:text-4xl font-bold text-white"
                data-testid="tenant-name"
                style={{ color: tenant?.branding?.primary_color || undefined }}
              >
                {tenant?.display_name}
              </h1>
              {tenant?.branding?.tagline ? (
                <p className="text-sm text-white/70" data-testid="tenant-tagline">{tenant.branding.tagline}</p>
              ) : (
                <p className="text-xs text-white/40 uppercase tracking-widest">
                  Powered by Link Ride · {tenant?.city}{tenant?.state ? `, ${tenant.state}` : ''}
                </p>
              )}
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[720px] space-y-2">
              <img
                src="/link-ride-logo.png"
                alt="Link Ride — Rideshare & Local Same Day Package Delivery"
                className="block mx-auto w-full h-auto rounded-xl shadow-[0_8px_40px_-12px_rgba(0,71,255,0.35)]"
                data-testid="link-hero-banner"
              />
              <div
                className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] sm:text-xs text-white/60"
                data-testid="brand-contact-strip"
              >
                <a
                  href="tel:+14845773677"
                  className="inline-flex items-center gap-1.5 hover:text-[#4D88FF] transition-colors"
                  data-testid="brand-contact-phone"
                >
                  <span className="font-mono tracking-tight">P · 484-577-3677</span>
                </a>
                <span className="text-white/20">·</span>
                <a
                  href="mailto:Link@LinkLitePro.com"
                  className="inline-flex items-center gap-1.5 hover:text-[#4D88FF] transition-colors"
                  data-testid="brand-contact-email"
                >
                  <span className="tracking-tight">E · Link@LinkLitePro.com</span>
                </a>
              </div>
            </div>
          )}
        </div>
        <div className="relative px-6 pb-5 flex items-center gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${
                step >= i ? "shadow-[0_0_10px_rgba(0,71,255,0.6)]" : "bg-white/10"
              }`}
              style={step >= i ? { backgroundColor: tenant?.branding?.primary_color || '#0047FF' } : undefined}
            />
          ))}
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto pb-16">
        {/* Step 1: Service */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Step 01</p>
              <h2 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
                {serviceCategory === "ride" ? "Book a ride" : "Send a package"}
              </h2>
              <p className="text-sm text-white/50 mt-1">
                {serviceCategory === "ride"
                  ? "Get where you\u2019re going — same fare every shift."
                  : "Same-day delivery within our service area."}
              </p>

              {/* Category toggle — separates Rideshare from Delivery so the
                  first page isn't a wall of options. Defaults to Rideshare
                  (higher-frequency use-case). */}
              <div
                className="mt-4 grid grid-cols-2 gap-1 p-1 rounded-2xl bg-[#121212] border border-white/5"
                data-testid="service-category-toggle"
                role="tablist"
              >
                <button
                  role="tab"
                  aria-selected={serviceCategory === "ride"}
                  onClick={() => {
                    setServiceCategory("ride");
                    setSelectedService(null);
                  }}
                  className={`py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-200 ${
                    serviceCategory === "ride"
                      ? "bg-[#0047FF] text-white shadow-[0_4px_16px_-4px_rgba(0,71,255,0.6)]"
                      : "text-white/50 hover:text-white/80"
                  }`}
                  data-testid="category-toggle-ride"
                >
                  Rideshare
                </button>
                <button
                  role="tab"
                  aria-selected={serviceCategory === "delivery"}
                  onClick={() => {
                    setServiceCategory("delivery");
                    setSelectedService(null);
                  }}
                  className={`py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-200 ${
                    serviceCategory === "delivery"
                      ? "bg-[#0047FF] text-white shadow-[0_4px_16px_-4px_rgba(0,71,255,0.6)]"
                      : "text-white/50 hover:text-white/80"
                  }`}
                  data-testid="category-toggle-delivery"
                >
                  Delivery
                </button>
              </div>

              <p className="text-[11px] uppercase tracking-[0.22em] text-[#4D88FF] font-bold mt-3" data-testid="pricing-tagline">
                No hidden fees · No tipping required
              </p>
              {serviceCategory === "ride" && (
                <button
                  onClick={() => navigate('/plans')}
                  className="mt-4 group flex items-center gap-2 text-left p-3 w-full rounded-xl bg-gradient-to-r from-[#FFD369]/10 to-[#0047FF]/10 border border-[#FFD369]/20 hover:border-[#FFD369]/40 transition-colors"
                  data-testid="step1-link-pass-banner"
                >
                  <div className="p-2 rounded-lg bg-[#FFD369]/15 group-hover:bg-[#FFD369]/25">
                    <Crown className="w-4 h-4 text-[#FFD369]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white">
                      Get to work for the same price every shift
                    </p>
                    <p className="text-[10px] text-white/50">
                      From $9.99/mo · 10% off · $15 stays $15 — even Saturday night
                    </p>
                  </div>
                  <span className="text-xs text-[#FFD369] font-bold">→</span>
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12 text-white/40">Loading services…</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {services
                  .filter((service) =>
                    serviceCategory === "ride"
                      ? service.id === "ride"
                      : service.id !== "ride"
                  )
                  .map((service) => {
                  const IconComponent = SERVICE_ICONS[service.id] || Package;
                  const isSelected = selectedService?.id === service.id;
                  const isZonePricing = service.pricingType === "zone";
                  // In rideshare mode there's only one service — always
                  // make it full-width so it doesn't look weirdly narrow.
                  const isFeatured = serviceCategory === "ride"
                    ? true
                    : (service.id === "rush_delivery" || service.id === "medium_package");
                  return (
                    <button
                      key={service.id}
                      onClick={() => { setSelectedService(service); setStep(2); }}
                      className={`relative overflow-hidden p-4 rounded-2xl border text-left transition-all duration-200 ${
                        isFeatured ? "col-span-2" : "col-span-1"
                      } ${
                        isSelected
                          ? "bg-[#0047FF]/10 border-[#0047FF]/40 scale-[1.01] shadow-[0_8px_32px_-12px_rgba(0,71,255,0.5)]"
                          : "bg-[#121212] border-white/5 hover:border-white/15 hover:bg-[#1A1A1A]"
                      }`}
                      data-testid={`service-${service.id}`}
                    >
                      <div className="absolute top-3 right-3">
                        <span
                          className={`text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
                            isZonePricing ? "bg-white/10 text-white/60" : "bg-[#0047FF]/20 text-[#4D88FF]"
                          }`}
                        >
                          {isZonePricing ? "Flat Zone" : "Per-Mile"}
                        </span>
                      </div>
                      <div className={`inline-flex p-2.5 rounded-xl mb-3 ${isSelected ? "bg-[#0047FF]/20" : "bg-white/5"}`}>
                        <IconComponent className={`w-5 h-5 ${isSelected ? "text-[#4D88FF]" : "text-white/70"}`} />
                      </div>
                      <h3
                        className={`font-bold tracking-tight ${isFeatured ? "text-lg" : "text-base"} ${
                          isSelected ? "text-white" : "text-white/90"
                        }`}
                        style={{ fontFamily: "Outfit, system-ui, sans-serif" }}
                      >
                        {service.name}
                      </h3>
                      <p className="text-xs text-white/50 mt-0.5 line-clamp-2">{service.description}</p>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold">From</span>
                        <span
                          className={`text-xl font-black tracking-tight ${isSelected ? "text-white" : "text-white/90"}`}
                          style={{ fontFamily: "Outfit, system-ui, sans-serif" }}
                        >
                          ${isZonePricing ? service.startingPrice : (service.minPrice ?? service.basePrice)}
                        </span>
                        {!isZonePricing && <span className="text-[11px] text-white/40 ml-1">+ ${service.perMile}/mi</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedService && (
              <Button
                onClick={() => setStep(2)}
                className="w-full py-6 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold tracking-wide rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                data-testid="continue-to-addresses"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}

        {/* Step 2: Addresses */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(1)}
                className="border-white/15 text-white/80 hover:bg-white/5"
                data-testid="back-to-step-1"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div className="ml-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-1">Step 02</p>
                <h2 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
                  Where to?
                </h2>
                <p className="text-sm text-white/50 mt-1">We'll calculate distance and price automatically</p>
              </div>
            </div>

            <Card className="bg-[#121212] border-white/5">
              <CardContent className="p-4 space-y-4">
                {/* Saved places quick-pick */}
                {isAuthenticated && savedAddresses.length > 0 && (
                  <div data-testid="saved-places-row">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 flex items-center gap-1.5">
                      <Bookmark className="w-3 h-3" /> Saved Places
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {savedAddresses.map((a) => (
                        <div key={a.id} className="inline-flex rounded-full overflow-hidden border border-white/10">
                          <button
                            onClick={() => setPickup({ address: a.address, coords: a.coords, city: a.city, state: a.state })}
                            className="px-3 py-1 text-xs font-medium text-[#00E676] hover:bg-[#00E676]/10 transition"
                            data-testid={`quickpick-pickup-${a.id}`}
                          >
                            <span className="opacity-60 mr-1">Pickup:</span>{a.label}
                          </button>
                          <div className="w-px bg-white/10" />
                          <button
                            onClick={() => setDropoff({ address: a.address, coords: a.coords, city: a.city, state: a.state })}
                            className="px-3 py-1 text-xs font-medium text-[#4D88FF] hover:bg-[#4D88FF]/10 transition"
                            data-testid={`quickpick-dropoff-${a.id}`}
                          >
                            <span className="opacity-60 mr-1">Drop:</span>{a.label}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/50 font-bold flex items-center gap-2 mb-2">
                    <MapPin className="w-3.5 h-3.5 text-[#00E676]" />
                    Pickup
                  </Label>
                  <AddressAutocomplete
                    value={pickup.address}
                    onChange={(address, coords, meta) =>
                      setPickup({ address, coords, city: meta?.city || "", state: meta?.state || "" })
                    }
                    placeholder="Start typing pickup address…"
                    accent="emerald"
                    testId="input-pickup"
                    anchor={userLocation}
                  />
                  {isAuthenticated && pickup.coords && !savedAddresses.some((a) => a.address === pickup.address) && (
                    <button
                      onClick={() => saveCurrentAddress(pickup, 'Home')}
                      className="mt-2 text-xs text-[#4D88FF] hover:text-[#7AADFF] font-medium flex items-center gap-1"
                      data-testid="save-pickup"
                    >
                      <Bookmark className="w-3 h-3" /> Save this address
                    </button>
                  )}
                </div>
                {/* Multi-stop waypoints */}
                {stops.map((stop, i) => (
                  <div key={`stop-${i}`} className="relative" data-testid={`stop-${i}`}>
                    <Label className="text-xs uppercase tracking-wider text-white/50 font-bold flex items-center justify-between gap-2 mb-2">
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-amber-400" />
                        Stop {i + 1}
                      </span>
                      <button
                        onClick={() => setStops(stops.filter((_, idx) => idx !== i))}
                        data-testid={`remove-stop-${i}`}
                        className="text-white/40 hover:text-red-400 text-[10px] uppercase tracking-wider"
                      >
                        Remove
                      </button>
                    </Label>
                    <AddressAutocomplete
                      value={stop.address}
                      onChange={(address, coords, meta) => {
                        const next = [...stops];
                        next[i] = { address, coords, city: meta?.city || "", state: meta?.state || "" };
                        setStops(next);
                      }}
                      placeholder={`Stop ${i + 1} address…`}
                      accent="emerald"
                      testId={`input-stop-${i}`}
                      anchor={pickup.coords || userLocation}
                    />
                    {stop.address && !stop.coords && (
                      <p className="mt-1.5 text-[11px] text-amber-300/90 flex items-center gap-1.5" data-testid={`stop-${i}-needs-pick`}>
                        <span className="text-amber-400">⚠</span>
                        Pick an address from the dropdown so we can route the driver to this stop.
                      </p>
                    )}
                  </div>
                ))}
                {pickup.coords && stops.length < 5 && (
                  <button
                    onClick={() => setStops([...stops, { address: "", coords: null, city: "", state: "" }])}
                    data-testid="add-stop"
                    className="w-full text-xs text-amber-300 hover:text-amber-200 font-semibold flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add stop ({stops.length}/5) · +$1 each
                  </button>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/50 font-bold flex items-center gap-2 mb-2">
                    <MapPin className="w-3.5 h-3.5 text-[#4D88FF]" />
                    Dropoff
                  </Label>
                  <AddressAutocomplete
                    value={dropoff.address}
                    onChange={(address, coords, meta) =>
                      setDropoff({ address, coords, city: meta?.city || "", state: meta?.state || "" })
                    }
                    placeholder="Start typing dropoff address…"
                    accent="blue"
                    testId="input-dropoff"
                    anchor={pickup.coords || userLocation}
                  />
                  {isAuthenticated && dropoff.coords && !savedAddresses.some((a) => a.address === dropoff.address) && (
                    <button
                      onClick={() => saveCurrentAddress(dropoff, 'Work')}
                      className="mt-2 text-xs text-[#4D88FF] hover:text-[#7AADFF] font-medium flex items-center gap-1"
                      data-testid="save-dropoff"
                    >
                      <Bookmark className="w-3 h-3" /> Save this address
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Route preview — only when both addresses are picked */}
            {pickup.coords && dropoff.coords && (
              <div className="rounded-2xl overflow-hidden border border-white/5" data-testid="route-preview">
                <LiveMap
                  pickupLocation={pickup.coords}
                  dropoffLocation={dropoff.coords}
                  height="180px"
                />
              </div>
            )}

            {/* Over-limit warning */}
            {overLimit && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-4" data-testid="over-limit-warning">
                <p className="text-sm font-medium text-red-300">
                  Outside service area ({distanceMiles?.toFixed(0)} mi)
                </p>
                <p className="text-xs text-red-300/70 mt-1">
                  Link operates within a {MAX_DISTANCE_MILES}-mile range. Please pick addresses closer together.
                </p>
              </div>
            )}

            {/* Live quote panel */}
            <Card
              className={`bg-[#121212] border transition-colors ${
                quote ? "border-[#0047FF]/30" : "border-white/5"
              }`}
              data-testid="quote-panel"
            >
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Estimated Price</p>
                  {routeLoading || quoteLoading ? (
                    <div className="flex items-center gap-2 mt-2 text-white/60">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Calculating…</span>
                    </div>
                  ) : quote ? (
                    <div>
                      <p className="text-3xl font-black text-white mt-1" style={{ fontFamily: "Outfit, system-ui" }} data-testid="quote-price">
                        ${quote.finalPrice.toFixed(2)}
                      </p>
                      <p className="text-xs text-white/50 mt-1" data-testid="quote-distance">
                        {distanceMiles?.toFixed(1)} mi · {selectedService?.name}
                      </p>
                      {quote.salesTax > 0 && (
                        <p className="text-[11px] text-white/40 mt-0.5" data-testid="quote-tax-line">
                          Includes ${quote.salesTax.toFixed(2)} ({quote.salesTaxPct}%) sales tax
                        </p>
                      )}
                      {quote.subscriberApplied ? (
                        <p className="text-[11px] text-[#FFD369] font-bold mt-1" data-testid="quote-subscriber-pill">
                          👑 {quote.subscriberApplied} · saved ${quote.subscriberDiscount.toFixed(2)}
                        </p>
                      ) : (
                        maxPlanDiscount > 0 && (
                          <button
                            onClick={() => navigate('/plans')}
                            className="mt-1 text-[11px] text-[#FFD369] hover:text-[#FFE093] font-bold underline-offset-2 hover:underline"
                            data-testid="quote-upsell-link"
                          >
                            👑 Would be ${(quote.finalPrice * (1 - maxPlanDiscount / 100)).toFixed(2)} with Link Pro →
                          </button>
                        )
                      )}

                      {/* Promo code — applies as a flat discount on top of the quote */}
                      {promoData ? (
                        <div className="mt-2 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/25" data-testid="promo-applied-row">
                          <div className="min-w-0">
                            <p className="text-[11px] font-bold text-emerald-300 truncate" data-testid="promo-applied-code">
                              🎟 {promoData.code} · −${promoData.discount_dollars.toFixed(2)}
                            </p>
                            <p className="text-[11px] text-emerald-200 font-semibold" data-testid="promo-applied-new-total">
                              Now ${promoData.new_total.toFixed(2)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={removePromoCode}
                            className="text-[10px] uppercase tracking-wider text-zinc-300 hover:text-white px-2 py-0.5 rounded-md border border-white/10 hover:bg-white/5"
                            data-testid="promo-remove-button"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 flex gap-1.5" data-testid="promo-input-row">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder="Promo code"
                            maxLength={32}
                            className="flex-1 min-w-0 px-2.5 py-1 text-xs bg-black/30 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 uppercase tracking-wider focus:outline-none focus:border-white/30"
                            data-testid="promo-code-input"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromoCode(); } }}
                          />
                          <button
                            type="button"
                            onClick={applyPromoCode}
                            disabled={promoApplying || !promoCode.trim()}
                            className="text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="promo-apply-button"
                          >
                            {promoApplying ? "…" : "Apply"}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-white/50 mt-2">Enter both addresses to see your price</p>
                  )}
                </div>
                {quote && (
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-[#00E676]/10 text-[#00E676] border-[#00E676]/25 font-medium">
                      Live Quote
                    </Badge>
                    {quote.pricingLocked && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30" data-testid="price-locked-badge">
                        🔒 Price Locked
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={() => setStep(3)}
              disabled={!canContinueFromAddresses}
              className="w-full py-6 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold tracking-wide rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="continue-to-schedule"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Schedule */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(2)}
                className="border-white/15 text-white/80 hover:bg-white/5"
                data-testid="back-to-step-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div className="ml-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-1">Step 03</p>
                <h2 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
                  When?
                </h2>
                <p className="text-sm text-white/50 mt-1">Go now or schedule for later</p>
              </div>
            </div>

            {/* ASAP / Later toggle */}
            {!asapAvailable && (
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 mb-3" data-testid="driver-busy-banner">
                <p className="text-xs font-bold text-amber-300">
                  🚗 Driver currently on a trip — ASAP not available
                </p>
                <p className="text-[11px] text-amber-300/70 mt-0.5">
                  Earliest pickup: {earliestSchedule ? earliestSchedule.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'soon'}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => asapAvailable && setScheduleMode("asap")}
                disabled={!asapAvailable}
                className={`p-4 rounded-2xl border text-left transition-all relative ${
                  !asapAvailable
                    ? "bg-[#0a0a0a] border-white/5 opacity-40 cursor-not-allowed"
                    : scheduleMode === "asap"
                      ? "bg-[#0047FF]/10 border-[#0047FF]/40 shadow-[0_8px_32px_-12px_rgba(0,71,255,0.5)]"
                      : "bg-[#121212] border-white/5 hover:border-white/15"
                }`}
                data-testid="schedule-asap"
              >
                {!asapAvailable && (
                  <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider text-amber-400 font-bold px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30">
                    Unavailable
                  </span>
                )}
                <div className={`inline-flex p-2.5 rounded-xl mb-3 ${scheduleMode === "asap" && asapAvailable ? "bg-[#0047FF]/20" : "bg-white/5"}`}>
                  <Lightning className={`w-5 h-5 ${scheduleMode === "asap" && asapAvailable ? "text-[#4D88FF]" : "text-white/70"}`} />
                </div>
                <p className="font-bold text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                  ASAP
                </p>
                <p className="text-xs text-white/50 mt-0.5">{asapAvailable ? "Nearest driver" : "Driver on a job"}</p>
              </button>
              <button
                onClick={() => setScheduleMode("later")}
                className={`p-4 rounded-2xl border text-left transition-all ${
                  scheduleMode === "later"
                    ? "bg-[#0047FF]/10 border-[#0047FF]/40 shadow-[0_8px_32px_-12px_rgba(0,71,255,0.5)]"
                    : "bg-[#121212] border-white/5 hover:border-white/15"
                }`}
                data-testid="schedule-later"
              >
                <div className={`inline-flex p-2.5 rounded-xl mb-3 ${scheduleMode === "later" ? "bg-[#0047FF]/20" : "bg-white/5"}`}>
                  <CalendarDays className={`w-5 h-5 ${scheduleMode === "later" ? "text-[#4D88FF]" : "text-white/70"}`} />
                </div>
                <p className="font-bold text-white" style={{ fontFamily: "Outfit, system-ui" }}>
                  Schedule
                </p>
                <p className="text-xs text-white/50 mt-0.5">Pick a date & time</p>
              </button>
            </div>

            {/* Calendar + time picker when "later" */}
            {scheduleMode === "later" && (
              <Card className="bg-[#121212] border-white/5">
                <CardContent className="p-4 space-y-4">
                  {earliestSchedule && (
                    <div className="rounded-lg bg-[#0047FF]/10 border border-[#0047FF]/30 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-[#4D88FF] font-bold">
                        Earliest available pickup
                      </p>
                      <p className="text-sm text-white mt-0.5">
                        {earliestSchedule.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      disabled={(d) => {
                        const earliestDay = earliestSchedule ? new Date(earliestSchedule) : new Date();
                        earliestDay.setHours(0, 0, 0, 0);
                        return d < earliestDay;
                      }}
                      className="text-white"
                      data-testid="schedule-calendar"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-white/50 font-bold flex items-center gap-2 mb-2">
                      <Clock className="w-3.5 h-3.5 text-[#4D88FF]" />
                      Time
                    </Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      min={
                        scheduledDate && earliestSchedule &&
                        scheduledDate.toDateString() === earliestSchedule.toDateString()
                          ? `${String(earliestSchedule.getHours()).padStart(2, "0")}:${String(earliestSchedule.getMinutes()).padStart(2, "0")}`
                          : undefined
                      }
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="bg-[#050505] border-white/10 text-white"
                      data-testid="schedule-time"
                    />
                    {scheduledDate && scheduledTime && earliestSchedule && (() => {
                      const picked = new Date(scheduledDate);
                      const [h, m] = scheduledTime.split(":");
                      picked.setHours(parseInt(h || "0", 10), parseInt(m || "0", 10), 0, 0);
                      if (picked < earliestSchedule) {
                        return (
                          <p className="text-[11px] text-amber-300 mt-1" data-testid="time-too-early-warning">
                            ⚠️ This is before the earliest available time. Please pick a later time.
                          </p>
                        );
                      }
                      return null;
                    })()}
                    {/* Busy time slots — shows the customer which windows are already taken
                        before they submit and get rejected by the overlap check. */}
                    {scheduledDate && busySlots.length > 0 && (() => {
                      const overlapping = (() => {
                        if (!scheduledTime) return null;
                        const [h, m] = scheduledTime.split(":").map(Number);
                        const pickedMin = h * 60 + m;
                        return busySlots.find((b) => {
                          const [sh, sm] = b.start.split(":").map(Number);
                          const [eh, em] = b.end.split(":").map(Number);
                          return pickedMin >= sh * 60 + sm && pickedMin < eh * 60 + em;
                        });
                      })();
                      return (
                        <div className="mt-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5 space-y-1.5" data-testid="busy-slots-panel">
                          <p className="text-[10px] uppercase tracking-wider font-bold text-amber-300">
                            Already booked on this day
                            {busyCapacity > 1 && (
                              <span className="ml-1 text-white/50 normal-case font-normal">
                                (capacity {busyCapacity} drivers)
                              </span>
                            )}
                          </p>
                          <ul className="text-[11px] text-amber-100/90 space-y-0.5">
                            {busySlots.map((b, i) => (
                              <li key={i} className="flex items-center gap-1.5" data-testid={`busy-slot-${i}`}>
                                <span className="text-amber-400">•</span>
                                <span className="font-mono">{fmt12(b.start)} – {fmt12(b.end)}</span>
                                <span className="text-white/40 capitalize">· {b.service_type}</span>
                              </li>
                            ))}
                          </ul>
                          {overlapping && (
                            <p className="text-[11px] text-red-300 font-bold pt-1" data-testid="time-conflict-warning">
                              ⛔ Your selected time ({fmt12(scheduledTime)}) overlaps {fmt12(overlapping.start)}–{fmt12(overlapping.end)}. Pick another time.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={() => setStep(4)}
              disabled={!canContinueFromSchedule}
              className="w-full py-6 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold tracking-wide rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="continue-to-contact"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 4: Contact + Review */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(3)}
                className="border-white/15 text-white/80 hover:bg-white/5"
                data-testid="back-to-step-3"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div className="ml-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-1">Step 04</p>
                <h2 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit, system-ui, sans-serif" }}>
                  Your info
                </h2>
                <p className="text-sm text-white/50 mt-1">So the driver can reach you</p>
              </div>
            </div>

            <Card className="bg-[#121212] border-white/5">
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label htmlFor="customer_name" className="text-xs uppercase tracking-wider text-white/50 font-bold">Name *</Label>
                  <Input
                    id="customer_name"
                    value={contact.customer_name}
                    onChange={(e) => setContact({ ...contact, customer_name: e.target.value })}
                    placeholder="Your name"
                    className="bg-[#050505] border-white/10 text-white mt-1"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone" className="text-xs uppercase tracking-wider text-white/50 font-bold">Phone *</Label>
                  <Input
                    id="customer_phone"
                    type="tel"
                    value={contact.customer_phone}
                    onChange={(e) => setContact({ ...contact, customer_phone: e.target.value })}
                    placeholder="Phone number"
                    className="bg-[#050505] border-white/10 text-white mt-1"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_email" className="text-xs uppercase tracking-wider text-white/50 font-bold">Email <span className="text-white/30 normal-case font-normal">(optional — for confirmation)</span></Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={contact.customer_email}
                    onChange={(e) => setContact({ ...contact, customer_email: e.target.value })}
                    placeholder="you@example.com"
                    className="bg-[#050505] border-white/10 text-white mt-1"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-white/50 font-bold">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={contact.notes}
                    onChange={(e) => setContact({ ...contact, notes: e.target.value })}
                    placeholder="Special instructions, package details…"
                    className="bg-[#050505] border-white/10 text-white mt-1"
                    rows={3}
                    data-testid="input-notes"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary card */}
            <Card className="bg-[#121212] border-[#0047FF]/30">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Summary</span>
                  {quote && (
                    <Badge className="bg-[#00E676]/10 text-[#00E676] border-[#00E676]/25 font-medium">Live Quote</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">Service</span>
                  <span className="text-white font-medium">{selectedService?.name}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">Distance</span>
                  <span className="text-white font-medium">{distanceMiles?.toFixed(1)} mi</span>
                </div>
                {(quote?.extraStops || 0) > 0 && (
                  <div className="flex items-center justify-between text-sm" data-testid="quote-extra-stops">
                    <span className="text-white/50">
                      Extra stops · {quote.extraStops} × ${quote.perStopFee?.toFixed(2)}
                    </span>
                    <span className="text-white font-medium">+${quote.extraStopFee?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">When</span>
                  <span className="text-white font-medium">
                    {scheduleMode === "asap" ? "ASAP" : `${toISODate(scheduledDate)} · ${scheduledTime}`}
                  </span>
                </div>

                {quote && isAuthenticated && (
                  <RedeemPointsPrompt onRedeemed={(c) => { setWalletCents(c || 0); setUseCredits(true); }} />
                )}

                {/* Wallet credits — partial redemption toggle (only if customer has any balance) */}
                {quote && walletCents > 0 && (() => {
                  // Stripe enforces a ~$0.50 minimum charge. Keep at least $1 of
                  // headroom so we never end up with a stranded sub-$1 remainder.
                  // If the wallet covers the full trip → apply 100%.
                  // Otherwise → cap credits at (total - $1) so Stripe always
                  // gets a chargeable amount.
                  const STRIPE_MIN_REMAINDER_CENTS = 100;
                  const totalCents = Math.round((quote.finalPrice || 0) * 100);
                  let applied = 0;
                  if (useCredits) {
                    if (walletCents >= totalCents) {
                      applied = totalCents;                // full coverage
                    } else if (paymentMethod === "stripe" && (totalCents - walletCents) < STRIPE_MIN_REMAINDER_CENTS) {
                      // Stripe remainder would be too small — cap credits
                      applied = Math.max(0, totalCents - STRIPE_MIN_REMAINDER_CENTS);
                    } else {
                      applied = Math.min(walletCents, totalCents);
                    }
                  }
                  const remainingCents = Math.max(0, totalCents - applied);
                  return (
                    <>
                      <div className="pt-3 border-t border-white/5 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => setUseCredits(v => !v)}
                          className={`flex-1 text-left rounded-lg px-3 py-2.5 border transition-colors ${
                            useCredits
                              ? "bg-amber-500/10 border-amber-500/40"
                              : "bg-zinc-800/40 border-white/5 hover:border-white/15"
                          }`}
                          data-testid="apply-credits-toggle"
                        >
                          <p className="text-xs font-bold text-white">
                            {useCredits ? "Applying wallet credits" : "Apply wallet credits"}
                          </p>
                          <p className="text-[11px] text-zinc-400 mt-0.5">
                            ${(walletCents / 100).toFixed(2)} available
                            {useCredits && applied > 0 && (
                              <span className="text-amber-300"> · −${(applied / 100).toFixed(2)} this trip</span>
                            )}
                          </p>
                        </button>
                        <div
                          className={`w-10 h-6 rounded-full p-0.5 transition-colors shrink-0 ${
                            useCredits ? "bg-amber-500" : "bg-zinc-700"
                          }`}
                          onClick={() => setUseCredits(v => !v)}
                          role="switch"
                          aria-checked={useCredits}
                        >
                          <div
                            className={`h-5 w-5 rounded-full bg-white transition-transform ${
                              useCredits ? "translate-x-4" : ""
                            }`}
                          />
                        </div>
                      </div>

                      {useCredits && applied > 0 && (
                        <div className="text-xs flex items-center justify-between text-amber-300" data-testid="credits-applied-row">
                          <span>Credits applied</span>
                          <span className="font-mono">−${(applied / 100).toFixed(2)}</span>
                        </div>
                      )}

                      <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                        <span className="text-white/60">
                          {useCredits && applied > 0 && remainingCents > 0
                            ? "Remaining due"
                            : useCredits && remainingCents === 0
                            ? "Paid with credits"
                            : "Total"}
                        </span>
                        <span
                          className="text-2xl font-black text-[#00E676]"
                          style={{ fontFamily: "Outfit, system-ui" }}
                          data-testid="summary-remaining-due"
                        >
                          ${(remainingCents / 100).toFixed(2)}
                        </span>
                      </div>
                    </>
                  );
                })()}

                {quote && walletCents === 0 && (
                  <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                    <span className="text-white/60">Total</span>
                    <span className="text-2xl font-black text-[#00E676]" style={{ fontFamily: "Outfit, system-ui" }}>
                      ${quote.finalPrice.toFixed(2)}
                    </span>
                  </div>
                )}

                {/* Loyalty preview — show points the customer will earn on this trip */}
                {quote && isAuthenticated && (() => {
                  const subtotal = quote.subtotal ?? (quote.finalPrice / 1.06);
                  const isSubscriber = !!(user?.subscription_plan_id);
                  const points = Math.floor(subtotal * (isSubscriber ? 1.5 : 1.0));
                  if (points <= 0) return null;
                  return (
                    <div
                      className="flex items-center justify-between text-xs text-yellow-300/90 bg-yellow-500/5 border border-yellow-500/20 rounded-lg px-3 py-2"
                      data-testid="loyalty-preview"
                    >
                      <span className="flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5" />
                        You'll earn after this trip
                      </span>
                      <span className="font-bold">
                        +{points} pts{isSubscriber && <span className="text-yellow-400/70 font-medium"> · 1.5×</span>}
                      </span>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Payment method picker */}
            {/* Payment method picker — hidden when wallet credits cover the whole trip */}
            {(() => {
              const STRIPE_MIN_REMAINDER_CENTS = 100;
              const totalCents = Math.round((quote?.finalPrice || 0) * 100);
              let applied = 0;
              if (useCredits) {
                if (walletCents >= totalCents) {
                  applied = totalCents;
                } else if (paymentMethod === "stripe" && (totalCents - walletCents) < STRIPE_MIN_REMAINDER_CENTS) {
                  applied = Math.max(0, totalCents - STRIPE_MIN_REMAINDER_CENTS);
                } else {
                  applied = Math.min(walletCents, totalCents);
                }
              }
              const remainingCents = Math.max(0, totalCents - applied);
              if (useCredits && remainingCents === 0 && totalCents > 0) {
                return (
                  <div
                    className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-center"
                    data-testid="paid-with-credits-banner"
                  >
                    <p className="text-sm font-bold text-amber-300">
                      Trip fully covered by your wallet credits
                    </p>
                    <p className="text-xs text-amber-400/70 mt-1">
                      No card needed — we'll deduct ${(applied / 100).toFixed(2)} when you confirm.
                    </p>
                  </div>
                );
              }
              return (
                <div data-testid="payment-method-picker">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">
                    Pay {applied > 0 ? "remaining " : ""}with
                  </p>
                  <div className="grid gap-2 grid-cols-3">
                    {/* Three ways to pay:
                        - stripe        → online card (upfront checkout)
                        - stripe_tap    → in-person card tap on arrival (was JIM)
                        - cash          → cash on arrival */}
                    {[
                      { id: 'stripe',     label: 'Pay Now', sub: 'Card online',        testId: 'pay-stripe' },
                      { id: 'stripe_tap', label: 'Tap on Arrival', sub: 'Card tap in person', testId: 'pay-stripe-tap' },
                      { id: 'cash',       label: 'Cash', sub: 'Pay driver in person',  testId: 'pay-cash' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setPaymentMethod(opt.id)}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          paymentMethod === opt.id
                            ? 'bg-[#0047FF]/10 border-[#0047FF]/40 shadow-[0_4px_20px_-8px_rgba(0,71,255,0.5)]'
                            : 'bg-[#121212] border-white/5 hover:border-white/15'
                        }`}
                        data-testid={opt.testId}
                      >
                        <p className={`text-sm font-bold ${paymentMethod === opt.id ? 'text-white' : 'text-white/80'}`} style={{ fontFamily: 'Outfit, system-ui' }}>
                          {opt.label}
                        </p>
                        <p className="text-[10px] text-white/50 mt-0.5 leading-tight">{opt.sub}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            <Button
              onClick={submitBooking}
              disabled={!canSubmit || submitting}
              className="w-full py-6 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold uppercase tracking-wide rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0 disabled:opacity-40"
              data-testid="submit-booking"
            >
              {(() => {
                if (submitting) return "Submitting…";
                // Re-compute the actual charge so the button never lies about
                // what Stripe will charge when credits are in play.
                const STRIPE_MIN_REMAINDER_CENTS = 100;
                const totalCents = Math.round((quote?.finalPrice || 0) * 100);
                let applied = 0;
                if (useCredits) {
                  if (walletCents >= totalCents) {
                    applied = totalCents;
                  } else if (paymentMethod === "stripe" && (totalCents - walletCents) < STRIPE_MIN_REMAINDER_CENTS) {
                    applied = Math.max(0, totalCents - STRIPE_MIN_REMAINDER_CENTS);
                  } else {
                    applied = Math.min(walletCents, totalCents);
                  }
                }
                const remainingCents = Math.max(0, totalCents - applied);
                const remainingDollars = (remainingCents / 100).toFixed(2);
                // Credits cover everything → no Stripe page, no card prompt.
                if (totalCents > 0 && remainingCents === 0) {
                  return `Book Now · Paid with Credits`;
                }
                if (paymentMethod === 'stripe') {
                  return `Pay $${remainingDollars} & Book`;
                }
                if (paymentMethod === 'stripe_tap') {
                  return `Book Now · Tap $${remainingDollars} on arrival`;
                }
                if (paymentMethod === 'jim') {
                  return `Book Now · Pay $${remainingDollars} on arrival`;
                }
                if (paymentMethod === 'cash') {
                  return `Book Now · Cash $${remainingDollars} on arrival`;
                }
                return scheduleMode === "asap" ? "Request Pickup Now" : "Schedule Pickup";
              })()}
            </Button>
          </div>
        )}
      </main>

      <footer className="px-5 py-6 text-center border-t border-white/5 mt-8">
        <p className="text-sm text-white/50">Questions? Call us</p>
        <Button
          onClick={() => window.open(`tel:${supportPhone}`, "_self")}
          variant="ghost"
          className="text-[#4D88FF] hover:text-[#7AADFF] mt-2"
        >
          <Phone className="w-4 h-4 mr-2" />
          {supportPhone}
        </Button>
        <div className="mt-3 text-xs text-white/30 flex items-center justify-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate("/features")}
            className="hover:text-white/60 transition-colors underline-offset-2 hover:underline"
            data-testid="footer-features-link"
          >
            What can Link Ride do? &middot; full feature list
          </button>
          <span className="text-white/10">·</span>
          <SupportTicketDialog
            triggerLabel="Report an issue"
            triggerClassName="hover:text-white/60 transition-colors underline-offset-2 hover:underline"
            initialEmail={user?.email || ""}
            testIdPrefix="footer-support"
          />
          <span className="text-white/10">·</span>
          <button
            type="button"
            onClick={() => navigate("/franchise")}
            className="hover:text-white/60 transition-colors underline-offset-2 hover:underline"
            data-testid="footer-franchise-link"
          >
            Franchise Link Ride in your city
          </button>
        </div>
      </footer>
    </div>
  );
}
